import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useAppStore } from '../../stores/appStore';
import { useSeason } from '../../hooks/useSeason';
import {
  hourKey,
  isFullScreen,
  msUntilNextHour,
  showDuration,
  showForMonth,
  showOverride,
  shouldPlayHourly,
  type ShowId,
} from '../../lib/hourlyShow';
import {
  ADVENT_MS,
  adventDayOverride,
  adventPlayedKey,
  adventScene,
  onAdventPlay,
  themeDate,
} from '../../lib/advent';
import { AdventSceneView, ShowScene } from './ShowScenes';

const HOUR_KEY = 'fink_show_hour';
const ADVENT_KEY = 'fink_advent_played';

type Item = { kind: 'show'; id: ShowId } | { kind: 'advent'; day: number };

function readStored(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeStored(key: string, value: string) {
  try {
    localStorage.setItem(key, value);
  } catch {
    /* private mode */
  }
}

/**
 * Plays the month's show ~15s after the dashboard opens, then at each hour.
 * December also plays today's advent scene once a day.
 */
export function HourlyShow() {
  const season = useSeason();
  const alerts = useAppStore((s) => s.activeAlerts.length);
  const [item, setItem] = useState<Item | null>(null);
  const layerRef = useRef<HTMLDivElement>(null);
  const [reduced, setReduced] = useState(
    () => window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );

  const seasonRef = useRef(season);
  seasonRef.current = season;
  const alertsRef = useRef(alerts);
  alertsRef.current = alerts;

  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const onChange = () => setReduced(mq.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  useEffect(() => {
    if (reduced) {
      setItem(null);
      return;
    }

    let alive = true;
    let hourTimer = 0;
    let endTimer = 0;
    let adventTimer = 0;
    let playing = false;
    const queue: Item[] = [];

    const begin = (next: Item) => {
      playing = true;
      if (!alive) return;
      setItem(next);
      const ms = next.kind === 'show' ? showDuration(next.id) : ADVENT_MS;
      window.clearTimeout(endTimer);
      endTimer = window.setTimeout(() => {
        const queued = queue.shift();
        if (queued) begin(queued);
        else {
          playing = false;
          if (alive) setItem(null);
        }
      }, ms);
    };

    const request = (next: Item) => {
      if (!alive || alertsRef.current > 0) return;
      if (playing) queue.push(next);
      else begin(next);
    };

    const tryHourly = () => {
      if (!alive) return;
      if (alertsRef.current > 0) {
        hourTimer = window.setTimeout(tryHourly, 20_000);
        return;
      }
      const now = new Date();
      if (shouldPlayHourly(now, readStored(HOUR_KEY))) {
        writeStored(HOUR_KEY, hourKey(now));
        request({ kind: 'show', id: showForMonth(seasonRef.current.month).id });
      }
      hourTimer = window.setTimeout(tryHourly, msUntilNextHour(new Date()) + 400);
    };

    const search = window.location.search;
    if (showOverride(search)) {
      request({ kind: 'show', id: showForMonth(seasonRef.current.month).id });
      hourTimer = window.setTimeout(tryHourly, msUntilNextHour(new Date()) + 400);
    } else {
      hourTimer = window.setTimeout(tryHourly, 15_000);
    }

    if (seasonRef.current.month === 12) {
      const themed = themeDate(new Date(), search);
      const day = themed.getDate();
      const forced = adventDayOverride(search) != null;
      const played = adventPlayedKey(themed.getFullYear(), day);
      if (day >= 1 && day <= 25) {
        adventTimer = window.setTimeout(() => {
          if (!alive) return;
          if (!forced && readStored(ADVENT_KEY) === played) return;
          if (!forced) writeStored(ADVENT_KEY, played);
          request({ kind: 'advent', day });
        }, forced ? 700 : 8000);
      }
    }

    const off = onAdventPlay((day) => {
      if (day < 1 || day > 25) return;
      const themed = themeDate(new Date(), window.location.search);
      writeStored(ADVENT_KEY, adventPlayedKey(themed.getFullYear(), themed.getDate()));
      request({ kind: 'advent', day });
    });

    return () => {
      alive = false;
      window.clearTimeout(hourTimer);
      window.clearTimeout(endTimer);
      window.clearTimeout(adventTimer);
      off();
      setItem(null);
    };
  }, [reduced]);

  useLayoutEffect(() => {
    const node = layerRef.current;
    if (!node || !item) return;
    const fullscreen = item.kind === 'show' && isFullScreen(item.id);

    const place = () => {
      if (fullscreen) {
        node.classList.add('show-layer--full');
        node.style.top = '';
        node.style.left = '';
        node.style.width = '';
        node.style.height = '';
        node.dataset.placed = 'screen';
        return true;
      }
      const stage = document.querySelector<HTMLElement>('[data-calendar-stage]');
      if (!stage) return false;
      const r = stage.getBoundingClientRect();
      if (r.width < 20 || r.height < 20) return false;
      node.classList.remove('show-layer--full');
      node.style.top = `${r.top}px`;
      node.style.left = `${r.left}px`;
      node.style.width = `${r.width}px`;
      node.style.height = `${r.height}px`;
      node.dataset.placed = 'calendar';
      return true;
    };

    let timer = 0;
    if (!place()) timer = window.setInterval(() => place() && window.clearInterval(timer), 50);
    window.addEventListener('resize', place);
    return () => {
      window.clearInterval(timer);
      window.removeEventListener('resize', place);
    };
  }, [item]);

  if (reduced || !item) return null;

  const scene = item.kind === 'advent' ? adventScene(item.day) : null;
  const showId = item.kind === 'show' ? item.id : `advent-${item.day}`;

  return (
    <div ref={layerRef} className="show-layer" data-show={showId} aria-hidden>
      {item.kind === 'show' ? <ShowScene id={item.id} /> : scene && <AdventSceneView scene={scene} />}
    </div>
  );
}
